import api from "@/shared/api/api";
import type { Creator } from "@/features/creators/services/creatorService";
import {
  type Payment,
  type PaginatedPaymentsResponse,
  type GetPaymentsParams,
  type CreatePaymentPayload,
  type UpdatePaymentPayload,
  type PaymentDocument,
  type CreatePaymentDocumentPayload,
  type UpdatePaymentDocumentPayload,
  type PaymentCreatorBankAccount,
  PaymentDocumentType,
  type PaginatedCreatorBankAccountsResponse,
  type CreateCreatorBankAccountPayload,
  type UpdateCreatorBankAccountPayload,
} from "../utils/types";

const buildPaymentPayload = (payload: Partial<CreatePaymentPayload>) => {
  const body: Record<string, unknown> = {};

  if (payload.creatorId !== undefined) body.creatorId = payload.creatorId;
  if (payload.creatorBankAccountId !== undefined) body.creatorBankAccountId = payload.creatorBankAccountId;
  if (payload.status !== undefined) body.status = payload.status;
  if (payload.value !== undefined) body.value = payload.value;
  if (payload.paymentDate !== undefined) body.paymentDate = payload.paymentDate;
  if (payload.referencePaymentDate !== undefined) {
    body.referencePaymentDate = payload.referencePaymentDate;
  }

  return body;
};

const buildPaymentDocumentFormData = (payload: {
  paymentId?: string;
  documentType?: PaymentDocumentType;
  description?: string;
  file?: File;
}) => {
  const formData = new FormData();
  const metadata: Record<string, unknown> = {};
  if (payload.documentType) metadata.documentType = payload.documentType;
  if (payload.description) metadata.description = payload.description;
  formData.append("data", JSON.stringify(metadata));
  if (payload.file) formData.append("file", payload.file, payload.file.name);
  return formData;
};

const mapSortField = (field?: GetPaymentsParams["sortBy"]) => field;

export const paymentApi = {
  list: async (params?: GetPaymentsParams, signal?: AbortSignal): Promise<PaginatedPaymentsResponse> => {
    const mapped: Record<string, unknown> = {};
    if (params?.page !== undefined) mapped.page = params.page;
    if (params?.limit !== undefined) mapped.limit = params.limit;
    if (params?.status && params.status !== "all") mapped.status = params.status;
    if (params?.creatorId) mapped.creatorId = params.creatorId;
    if (params?.creatorBankAccountId) mapped.creatorBankAccountId = params.creatorBankAccountId;
    if (params?.value !== undefined) mapped.value = params.value;
    if (params?.paymentDateStart) mapped.paymentDateStart = params.paymentDateStart;
    if (params?.paymentDateEnd) mapped.paymentDateEnd = params.paymentDateEnd;
    if (params?.referencePaymentDateStart) mapped.referencePaymentDateStart = params.referencePaymentDateStart;
    if (params?.referencePaymentDateEnd) mapped.referencePaymentDateEnd = params.referencePaymentDateEnd;
    if (params?.search) mapped.search = params.search;
    if (params?.sortBy) mapped.sortBy = mapSortField(params.sortBy);
    if (params?.order) mapped.order = params.order;

    return api<PaginatedPaymentsResponse>({
      url: "/v1/payments",
      method: "GET",
      params: mapped,
      signal,
    });
  },

  get: async (id: string, signal?: AbortSignal): Promise<Payment> => {
    return api<Payment>({
      url: `/v1/payments/${id}`,
      method: "GET",
      signal,
    });
  },

  create: async (payload: CreatePaymentPayload): Promise<Payment> => {
    return api<Payment>({
      url: "/v1/payments",
      method: "POST",
      data: buildPaymentPayload(payload),
    });
  },

  update: async (payload: UpdatePaymentPayload): Promise<Payment> => {
    const { id, ...rest } = payload;
    return api<Payment>({
      url: `/v1/payments/${id}`,
      method: "PUT",
      data: buildPaymentPayload(rest),
    });
  },

  remove: async (id: string): Promise<{ id: string; deletedAt?: string }> => {
    return api({
      url: `/v1/payments/${id}`,
      method: "DELETE",
    });
  },

  documents: {
    list: async (paymentId: string, signal?: AbortSignal): Promise<PaymentDocument[]> => {
      return api<PaymentDocument[]>({
        url: `/v1/payments/${paymentId}/documents`,
        method: "GET",
        signal,
      });
    },

    create: async (payload: CreatePaymentDocumentPayload): Promise<PaymentDocument> => {
      const form = buildPaymentDocumentFormData(payload);
      return api<PaymentDocument>({
        url: `/v1/payments/${payload.paymentId}/documents`,
        method: "POST",
        data: form,
      });
    },

    update: async (payload: UpdatePaymentDocumentPayload): Promise<PaymentDocument> => {
      const { id, ...rest } = payload;
      const form = buildPaymentDocumentFormData(rest);
      return api<PaymentDocument>({
        url: `/v1/payments/${payload.paymentId}/documents/${id}`,
        method: "PUT",
        data: form,
      });
    },

    remove: async (paymentId: string, id: string): Promise<{ id: string; deletedAt?: string }> => {
      return api({
        url: `/v1/payments/${paymentId}/documents/${id}`,
        method: "DELETE",
      });
    },
  },

  creators: {
    list: async (signal?: AbortSignal): Promise<Creator[]> => {
      const response = await api<{ data: Creator[] }>({
        url: "/v1/creators",
        method: "GET",
        params: { limit: 9999 },
        signal,
      });
      return response.data;
    },
  },

  bankAccounts: {
    list: async (
      params?: { creatorId?: string; page?: number; limit?: number; search?: string; active?: boolean; default?: boolean },
      signal?: AbortSignal,
    ): Promise<PaginatedCreatorBankAccountsResponse> => {
      const query: Record<string, unknown> = {};
      if (params?.creatorId) query.creatorId = params.creatorId;
      if (params?.page !== undefined) query.page = params.page;
      if (params?.limit !== undefined) {
        query.limit = params.limit;
      } else {
        query.limit = 50;
      }
      if (params?.search) query.search = params.search;
      if (params?.active !== undefined) query.active = params.active;
      if (params?.default !== undefined) query.default = params.default;
      return api<PaginatedCreatorBankAccountsResponse>({
        url: "/v1/creator-bank-accounts",
        method: "GET",
        params: query,
        signal,
      });
    },
    create: async (payload: CreateCreatorBankAccountPayload): Promise<PaymentCreatorBankAccount> => {
      return api<PaymentCreatorBankAccount>({
        url: "/v1/creator-bank-accounts",
        method: "POST",
        data: payload,
      });
    },
    update: async (payload: UpdateCreatorBankAccountPayload): Promise<PaymentCreatorBankAccount> => {
      const { id, ...rest } = payload;
      return api<PaymentCreatorBankAccount>({
        url: `/v1/creator-bank-accounts/${id}`,
        method: "PUT",
        data: rest,
      });
    },
    remove: async (id: string): Promise<{ id: string; deletedAt?: string }> => {
      return api({
        url: `/v1/creator-bank-accounts/${id}`,
        method: "DELETE",
      });
    },
  },
};
